import { getObjectEntries, getObjectKeys } from '@festina-life/core'
import { useRef, useState } from 'react'

type FieldState = {
  value: string
  previousExternalValue?: string
  isPartial: boolean
}

type FieldsState<Field extends string> = {
  fields: Record<Field, FieldState>
  dateFieldKey: number
}

const mapFields = <Field extends string, From, To>(
  record: Record<Field, From>,
  map: (value: From) => To,
) =>
  // Not `getObjectFromEntries`: it makes keys optional and can't resolve the generic `Field`.
  // TypeScript can't tell whether an array of entries covers every key.
  Object.fromEntries(
    getObjectEntries(record).map(([field, value]) => [field, map(value)]),
  ) as Record<Field, To>

const withField = <Field extends string>(
  state: FieldsState<Field>,
  { field, ...update }: Partial<FieldState> & { field: Field },
): FieldsState<Field> => ({
  ...state,
  fields: { ...state.fields, [field]: { ...state.fields[field], ...update } },
})

// An empty value while partial is our own emit; syncing it would wipe the typed segments.
const shouldSyncExternal = (field: FieldState, value: string | undefined) =>
  !field.isPartial || !!value

const syncExternal = <Field extends string>({
  state,
  valuesExternal,
  onExternalSync,
}: {
  state: FieldsState<Field>
  valuesExternal: Record<Field, string | undefined>
  onExternalSync?: (field: Field) => void
}) => {
  let synced = state
  for (const field of getObjectKeys(valuesExternal)) {
    const value = valuesExternal[field]
    const fieldState = state.fields[field]
    if (value === fieldState.previousExternalValue) {
      continue
    }
    const shouldSync = shouldSyncExternal(fieldState, value)
    if (shouldSync) {
      onExternalSync?.(field)
    }
    synced = withField(synced, {
      field,
      previousExternalValue: value,
      ...(shouldSync && { value: value ?? '', isPartial: false }),
    })
  }
  return synced
}

/** Holds the value, partial-input state and shared remount key of date fields, kept in sync with their external values. */
export const useDateFieldsState = <Field extends string>(
  valuesExternal: Record<Field, string | undefined>,
  onExternalSync?: (field: Field) => void,
) => {
  const [state, setState] = useState<FieldsState<Field>>(() => ({
    fields: mapFields(valuesExternal, (value) => ({
      value: value ?? '',
      previousExternalValue: value,
      isPartial: false,
    })),
    dateFieldKey: 0,
  }))
  // Typing the last year digit can move focus before re-rendering, so blur must read the latest state.
  const stateRef = useRef(state)
  const update = (next: FieldsState<Field>) => {
    stateRef.current = next
    setState(next)
  }

  const synced = syncExternal({ state, valuesExternal, onExternalSync })
  if (synced !== state) {
    update(synced)
  }

  return {
    fields: state.fields,
    dateFieldKey: state.dateFieldKey,
    getLatestField: (field: Field) => stateRef.current.fields[field],
    change: (field: Field, value: string) =>
      update(withField(stateRef.current, { field, value, isPartial: false })),
    markPartial: (field: Field) =>
      update(withField(stateRef.current, { field, isPartial: true })),
    completePartial: (field: Field) =>
      update(withField(stateRef.current, { field, isPartial: false })),
    clear: () =>
      update({
        fields: mapFields(stateRef.current.fields, (field) => ({
          ...field,
          value: '',
          isPartial: false,
        })),
        // Remounting the date fields is the only way to drop partially typed segments.
        dateFieldKey: stateRef.current.dateFieldKey + 1,
      }),
  }
}

/** Single-field variant of {@link useDateFieldsState}. */
export const useDateFieldState = (
  valueExternal: string | undefined,
  onExternalSync: () => void,
) => {
  const { fields, dateFieldKey, change, markPartial, completePartial, clear } =
    useDateFieldsState({ value: valueExternal }, onExternalSync)

  return {
    internalValue: fields.value.value,
    isPartial: fields.value.isPartial,
    dateFieldKey,
    change: (value: string) => change('value', value),
    markPartial: () => markPartial('value'),
    completePartial: () => completePartial('value'),
    clear,
  }
}
